import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CALENDAR_UNITS, METER_KINDS, TRIGGER_LOGICS, newId, type CalendarUnit, type MeterKind, type ServicePlanPublishV1, type TriggerLogic } from '@gk/contracts'
import { useEngineState } from '../club/ClubProvider.tsx'
import { typeLabel, useAssetTypeLabels, useAssetTypes, useAssets } from '../data/fleet.ts'
import { useMeterData, usePlans, usePlanVersions, type PlanRow, type PlanVersionRow } from '../data/meters.ts'
import { machineName, meterKindsOf } from '../data/workshopUi.ts'
import { fmtNum, parseDecimal } from '../lib/format.ts'
import { BackLink, Banner, DisabledHint, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { useCloseOnPending } from './workshop/shared.tsx'

const usesCalendar = (l: TriggerLogic) => l !== 'METER_ONLY'
const usesMeter = (l: TriggerLogic) => l !== 'CALENDAR_ONLY'

/** Service plans (GK-008, I8, I10): a plan is a stable header, every change a NEW immutable version. The form only builds rule
 *  combinations the server accepts; the server and the table CHECK refuse anything else. */
export function ServicePlansPage() {
  const { t, i18n } = useTranslation()
  const plans = usePlans()
  const versions = usePlanVersions()
  const assets = useAssets()
  const labels = useAssetTypeLabels(i18n.language)
  const publish = useWriteAccess('service_plan.publish')
  const online = useEngineState().online !== false
  const [editing, setEditing] = useState<{ plan?: PlanRow; latest?: PlanVersionRow } | null>(null)
  const [kept, setKept] = useState(false)
  const latest = useMemo(() => {
    const m = new Map<string, PlanVersionRow>()
    for (const v of versions ?? []) { const cur = m.get(v.plan_id); if (!cur || v.version_no > cur.version_no) m.set(v.plan_id, v) }
    return m
  }, [versions])
  const tagOf = new Map((assets ?? []).map((a) => [a.id, a.readable_tag]))
  const cal = (v: PlanVersionRow) => t(`service.interval.${v.calendar_unit}`, { count: v.calendar_every ?? 0 })
  const met = (v: PlanVersionRow) => `${fmtNum(v.meter_interval ?? 0, i18n.language)} ${t(`meters.units.${v.meter_slot_kind === 'odometer' ? 'km' : v.meter_slot_kind === 'cycles' ? 'count' : 'h'}`)}`

  return (
    <section className="flex flex-col gap-4" data-testid="plans-page">
      <BackLink to="/service">← {t('service.back')}</BackLink>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('service.plans')}</h1>
        {publish.allowed && online && !editing && <button className="btn btn-primary w-full sm:w-auto" onClick={() => { setKept(false); setEditing({}) }} data-testid="plan-new">{t('service.planNew')}</button>}
      </div>
      {kept && <PendingBanner />}
      {plans && plans.length === 0 && !editing && <p className="opacity-70">{t('service.noPlans')}</p>}
      {editing && <PlanForm key={editing.plan?.id ?? 'new'} plan={editing.plan} base={editing.latest} onDone={() => setEditing(null)} onPending={() => { setKept(true); setEditing(null) }} />}
      <ul className="flex flex-col gap-3" data-testid="plan-list">
        {(plans ?? []).map((p) => {
          const v = latest.get(p.id)
          return (
            <li key={p.id} className="card flex flex-col gap-2" data-testid="plan-row">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-lg font-bold">{p.name}</p>
                  <p className="meta">{p.asset_id ? t('service.forAsset', { tag: tagOf.get(p.asset_id) ?? '…' }) : t('service.forType', { code: typeLabel(labels, p.asset_type_code) })}</p>
                </div>
                {v && <Pill tone="gray">{t('service.version', { n: v.version_no })}</Pill>}
              </div>
              {v && <p className="text-sm">{t(`service.summary.${v.trigger_logic}`, { cal: v.calendar_every ? cal(v) : '', meter: v.meter_interval ? met(v) : '' })}</p>}
              {v && <p className="meta">{t(`service.sources.${v.source}`)} · {v.evidence_note}</p>}
              {publish.allowed && online && !editing && <button className="btn" onClick={() => { setKept(false); setEditing({ plan: p, latest: v }) }} data-testid="plan-version">{t('service.planVersion')}</button>}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** The plan form (M23 item 38): the target as two cards, the category a select of the club's type labels, short rule words, the counter kind
 *  hidden when the machine has one kind, a fixed "Unité" label, the reason beside a grey Publier. */
function PlanForm({ plan, base, onDone, onPending }: { plan?: PlanRow; base?: PlanVersionRow; onDone: () => void; onPending: () => void }) {
  const { t, i18n } = useTranslation()
  const act = useWorkshopAction()
  const assets = useAssets() ?? []
  const types = useAssetTypes() ?? []
  const labels = useAssetTypeLabels(i18n.language)
  const { slots } = useMeterData()
  const [ids] = useState(() => ({ plan: plan?.id ?? newId(), version: newId() }))
  const [name, setName] = useState(plan?.name ?? '')
  const [target, setTarget] = useState<'asset' | 'type'>('type')
  const [assetId, setAssetId] = useState('')
  const [typeCode, setTypeCode] = useState('')
  const [logic, setLogic] = useState<TriggerLogic>(base?.trigger_logic ?? 'OR')
  const [every, setEvery] = useState(String(base?.calendar_every ?? 12))
  const [unit, setUnit] = useState<CalendarUnit>(base?.calendar_unit ?? 'month')
  const [kind, setKind] = useState<MeterKind>(base?.meter_slot_kind ?? 'engine_hours')
  const [interval, setIntervalValue] = useState(base?.meter_interval ? String(base.meter_interval) : '250')
  const [source, setSource] = useState<'manufacturer' | 'local_approved'>(base?.source ?? 'manufacturer')
  const [evidence, setEvidence] = useState(base?.evidence_note ?? '')
  const [formError, setFormError] = useState<string | null>(null)
  useCloseOnPending(act.pending, onPending)
  // the machine's own counter kinds: one kind needs no select (the plan can only mean that one)
  const machineKinds = !plan && target === 'asset' && assetId ? meterKindsOf(slots, assetId) : plan?.asset_id ? meterKindsOf(slots, plan.asset_id) : []
  const kindUsed: MeterKind = machineKinds.length === 1 ? machineKinds[0]! : kind
  const activeTypes = types.filter((ty) => !ty.archived_at)

  function submit() {
    setFormError(null)
    const payload: ServicePlanPublishV1 = { plan_id: ids.plan, version_id: ids.version, trigger_logic: logic, source, evidence_note: evidence.trim() }
    if (!plan) {
      payload.name = name.trim()
      if (target === 'asset') { if (!assetId) { setFormError(t('service.needAsset')); return } payload.asset_id = assetId }
      else { if (!typeCode.trim()) { setFormError(t('service.needTarget')); return } payload.asset_type_code = typeCode.trim() }
    }
    if (usesCalendar(logic)) { payload.calendar_every = Number.parseInt(every, 10); payload.calendar_unit = unit }
    if (usesMeter(logic)) { payload.meter_slot_kind = kindUsed; const n = parseDecimal(interval); if (n !== null) payload.meter_interval = n }
    void act.run('service_plan.publish@1', payload, 'service.published').then((ok) => { if (ok) onDone() })
  }

  const missing = !evidence.trim() ? t('service.publishHint') : !plan && !name.trim() ? t('service.nameHint') : null
  return (
    <form className="card flex flex-col gap-3" data-testid="plan-form" onSubmit={(e) => { e.preventDefault(); submit() }}>
      <h2 className="text-lg font-bold">{plan ? `${plan.name} · ${t('service.planVersion')}` : t('service.planNew')}</h2>
      {plan && <p className="meta">{t('service.versionNote')}</p>}
      {!plan && (
        <>
          <Field label={t('service.planName')} htmlFor="plan-name"><input id="plan-name" className="input" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="plan-name" /></Field>
          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm font-semibold">{t('service.target')}</legend>
            <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={t('service.target')} data-testid="plan-target" data-value={target}>
              {(['type', 'asset'] as const).map((opt) => (
                <button key={opt} type="button" role="radio" aria-checked={target === opt} className={`btn ${target === opt ? 'btn-primary' : ''}`} onClick={() => setTarget(opt)} data-testid={`plan-target-${opt}`}>
                  {t(opt === 'asset' ? 'service.targetAsset' : 'service.targetType')}
                </button>
              ))}
            </div>
          </fieldset>
          {target === 'asset' ? (
            <Field label={t('service.targetAsset')} htmlFor="plan-asset">
              <select id="plan-asset" className="input" value={assetId} onChange={(e) => setAssetId(e.target.value)} data-testid="plan-asset">
                <option value="">{t('service.chooseAsset')}</option>
                {assets.filter((a) => a.lifecycle !== 'retired').map((a) => <option key={a.id} value={a.id}>{a.readable_tag}{machineName(a) ? ` — ${machineName(a)}` : ''}</option>)}
              </select>
            </Field>
          ) : (
            <Field label={t('service.typeCode')} htmlFor="plan-type">
              <select id="plan-type" className="input" value={typeCode} onChange={(e) => setTypeCode(e.target.value)} data-testid="plan-type">
                <option value="">{t('service.chooseType')}</option>
                {activeTypes.map((ty) => <option key={ty.id} value={ty.code}>{typeLabel(labels, ty.code)}</option>)}
              </select>
            </Field>
          )}
        </>
      )}
      <Field label={t('service.logic')} htmlFor="plan-logic">
        <select id="plan-logic" className="input" value={logic} onChange={(e) => setLogic(e.target.value as TriggerLogic)} data-testid="plan-logic">
          {TRIGGER_LOGICS.map((l) => <option key={l} value={l}>{t(`service.logicShort.${l}`)}</option>)}
        </select>
      </Field>
      {usesMeter(logic) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {machineKinds.length === 1
            ? <p className="meta self-end" data-testid="plan-kind-fixed">{t('service.meterKind')} : {t(`meters.kinds.${kindUsed}`)}</p>
            : (
              <Field label={t('service.meterKind')} htmlFor="plan-kind">
                <select id="plan-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as MeterKind)} data-testid="plan-kind">{METER_KINDS.map((k) => <option key={k} value={k}>{t(`meters.kinds.${k}`)}</option>)}</select>
              </Field>
            )}
          <Field label={t('service.meterInterval')} htmlFor="plan-interval"><input id="plan-interval" className="input" inputMode="decimal" value={interval} onChange={(e) => setIntervalValue(e.target.value)} data-testid="plan-interval" /></Field>
        </div>
      )}
      {usesCalendar(logic) && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('service.every')} htmlFor="plan-every"><input id="plan-every" className="input" inputMode="numeric" value={every} onChange={(e) => setEvery(e.target.value)} data-testid="plan-every" /></Field>
          <Field label={t('service.unitLabel')} htmlFor="plan-unit">
            <select id="plan-unit" className="input" value={unit} onChange={(e) => setUnit(e.target.value as CalendarUnit)}>{CALENDAR_UNITS.map((u) => <option key={u} value={u}>{t(`service.unit.${u}`)}</option>)}</select>
          </Field>
        </div>
      )}
      <Field label={t('service.source')} htmlFor="plan-source">
        <select id="plan-source" className="input" value={source} onChange={(e) => setSource(e.target.value as 'manufacturer' | 'local_approved')}>
          <option value="manufacturer">{t('service.sources.manufacturer')}</option><option value="local_approved">{t('service.sources.local_approved')}</option>
        </select>
      </Field>
      <Field label={t('service.evidence')} hint={t('service.evidenceHelp')} htmlFor="plan-evidence"><input id="plan-evidence" className="input" required maxLength={500} value={evidence} onChange={(e) => setEvidence(e.target.value)} data-testid="plan-evidence" /></Field>
      {(formError || act.error) && <Banner tone="error">{formError ?? act.error}</Banner>}
      <DisabledHint show={!!missing} testId="plan-publish-hint">{missing}</DisabledHint>
      <div className="grid grid-cols-2 gap-3">
        <button type="button" className="btn" onClick={onDone}>{t('common.cancel')}</button>
        <button className="btn btn-primary" disabled={act.busy || !!missing} data-testid="plan-publish">{t('service.publish')}</button>
      </div>
    </form>
  )
}
